import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@heidi/design-system/alert-dialog';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';

export interface ChatRowDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

/**
 * Nav-local delete confirmation for a chat row's action menu.
 *
 * Deliberately not `DeleteChatDialog` — that component lives in
 * `src/components/chat/components/` and the nav must not import it (spec
 * constraint C2). Mirrors the existing `deleteChatModal.*` English under new
 * react-intl ids rather than reading those i18next keys, since new files may
 * not add `useTranslations` call sites (spec C5, amended).
 */
export const ChatRowDeleteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: ChatRowDeleteDialogProps) => {
  const intl = useIntl();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {intl.formatMessage(navV3Messages.chatRowDeleteDialogTitle)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {intl.formatMessage(navV3Messages.chatRowDeleteDialogDescription)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {intl.formatMessage(navV3Messages.chatRowDeleteDialogCancel)}
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isDeleting}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {isDeleting
              ? intl.formatMessage(navV3Messages.chatRowDeleteDialogDeleting)
              : intl.formatMessage(navV3Messages.chatRowDeleteDialogConfirm)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
